import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import './CustomAgentBuilder.css';

import CustomAgentToolbar from '../../../components/custom-agent/CustomAgentToolbar';
import NodePalette from '../../../components/custom-agent/NodePalette';
import NodeConfigPanel from '../../../components/custom-agent/NodeConfigPanel';
import DeployModal from '../../../components/custom-agent/DeployModal';
import TestCustomAgentModal from '../../../components/custom-agent/TestCustomAgentModal';
import customAgentService from '../../../services/customAgent';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';

// Import custom node types
import TriggerNode from '../../../components/custom-agent/nodes/TriggerNode';
import ConditionNode from '../../../components/custom-agent/nodes/ConditionNode';
import AIActionNode from '../../../components/custom-agent/nodes/AIActionNode';

const nodeTypes = {
  trigger: TriggerNode,
  cognition: ConditionNode,
  action: AIActionNode
};

const FIT_VIEW_OPTIONS = {
  padding: 0.3,
  maxZoom: 0.85
};

const CustomAgentBuilder = () => {
  // Route parameter is 'id', not 'customAgentId'
  const { id } = useParams();
  const customAgentId = id; // Use 'id' from route params
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isPaletteOpen, setIsPaletteOpen] = useState(true);
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
  const [currentCustomAgent, setCurrentCustomAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [deployStatus, setDeployStatus] = useState(null); // 'deploying', 'triggering', 'success', 'error'

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Load customAgent if editing (skip if customAgentId is "new")
  useEffect(() => {
    if (customAgentId && customAgentId !== 'new') {
      loadCustomAgent();
    } else if (customAgentId === 'new') {
      // Reset state for new customAgent
      setCurrentCustomAgent(null);
      setNodes([]);
      setEdges([]);
    }
  }, [customAgentId]);

  useEffect(() => {
    if (!reactFlowInstance || nodes.length === 0) return;
    // Re-apply a restrained fitView whenever nodes load/change to avoid zooming too far in
    reactFlowInstance.fitView(FIT_VIEW_OPTIONS);
  }, [reactFlowInstance, nodes.length]);

  const loadCustomAgent = async () => {
    try {
      setLoading(true);
      console.log('Loading customAgent:', customAgentId);
      const customAgent = await customAgentService.getCustomAgent(customAgentId);
      console.log('CustomAgent loaded from API:', { id: customAgent?.id, name: customAgent?.name, hasName: !!customAgent?.name });
      
      if (!customAgent) {
        console.error('CustomAgent not found');
        showError('CustomAgent not found');
        return;
      }
      
      // Validate customAgent has required fields
      if (!customAgent.id) {
        console.error('CustomAgent missing id field');
        showError('Invalid customAgent data');
        return;
      }
      
      // Ensure nodes have proper structure with data.config
      const normalizedNodes = (customAgent.nodes || []).map(node => {
        // If node doesn't have data.config but has config at root, move it
        if (node.config && !node.data?.config) {
          return {
            ...node,
            data: {
              ...node.data,
              config: node.config
            },
            config: undefined // Remove root-level config
          };
        }
        // Ensure data.config exists even if empty
        return {
          ...node,
          data: {
            ...node.data,
            config: node.data?.config || {}
          }
        };
      });
      
      // Ensure customAgent has all required fields with proper defaults
      const normalizedCustomAgent = {
        ...customAgent,
        id: customAgent.id || customAgentId,
        name: customAgent.name || 'Untitled Custom Agent',
        description: customAgent.description || '',
        status: customAgent.status || 'draft',
        nodes: normalizedNodes,
        edges: customAgent.edges || [],
        executionMode: customAgent.executionMode || 'manual',
        version: customAgent.version || 1
      };
      
      console.log('Normalized customAgent:', { id: normalizedCustomAgent.id, name: normalizedCustomAgent.name, hasName: !!normalizedCustomAgent.name });
      
      setCurrentCustomAgent(normalizedCustomAgent);
      setNodes(normalizedNodes);
      setEdges(customAgent.edges || []);
      console.log('CustomAgent state updated successfully:', { id: normalizedCustomAgent.id, name: normalizedCustomAgent.name });
    } catch (err) {
      console.error('Error loading customAgent:', err);
      showError('Failed to load customAgent');
    } finally {
      setLoading(false);
    }
  };

  // Handle node changes - intercept deletions to show confirmation
  const onNodesChange = useCallback(
    (changes) => {
      // Check if any of the changes are node removals
      const hasRemoval = changes.some((change) => change.type === 'remove');
      
      if (hasRemoval) {
        // Prevent React Flow's default deletion and use our confirmation dialog instead
        // The deletion will be handled by our keyboard event listener or delete button
        return;
      }
      
      // Apply all other changes (position, selection, etc.)
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes]
  );

  // Handle edge changes
  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  // Handle new connections
  const onConnect = useCallback(
    (connection) => {
      const edge = {
        ...connection,
        type: 'smoothstep',
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed
        }
      };
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges]
  );

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setIsConfigPanelOpen(true);
    // Update node selection state
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        selected: n.id === node.id
      }))
    );
  }, []);

  // Handle pane click (deselect when clicking canvas)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setIsConfigPanelOpen(false);
    // Clear all selections
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        selected: false
      }))
    );
  }, []);

  // Handle node deletion
  const handleDeleteNode = useCallback(() => {
    if (!selectedNode) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${selectedNode.data.label}"?\n\nThis action cannot be undone.`
    );

    if (confirmDelete) {
      // Remove the node
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      // Remove any connected edges
      setEdges((eds) =>
        eds.filter(
          (e) => e.source !== selectedNode.id && e.target !== selectedNode.id
        )
      );
      // Close config panel and clear selection
      setSelectedNode(null);
      setIsConfigPanelOpen(false);
    }
  }, [selectedNode]);

  // Handle ESC key to deselect and Delete/Backspace to delete
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't handle keyboard shortcuts if user is typing in an input field
      const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName);
      
      // ESC key - deselect node
      if (event.key === 'Escape' && !isTyping) {
        setSelectedNode(null);
        setIsConfigPanelOpen(false);
        // Clear all selections
        setNodes((nds) =>
          nds.map((n) => ({
            ...n,
            selected: false
          }))
        );
      }
      
      // Delete or Backspace key - delete selected node
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedNode && !isTyping) {
        // Prevent backspace from navigating back in browser
        event.preventDefault();
        handleDeleteNode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, handleDeleteNode]);

  // Handle node drag
  const onNodeDragStop = useCallback((event, node) => {
    // Update node position
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === node.id) {
          return { ...n, position: node.position };
        }
        return n;
      })
    );
  }, []);

  // Add node from palette (click)
  const onNodeAdd = useCallback(
    (nodeType) => {
      const newNode = {
        id: `node_${Date.now()}`,
        type: getReactFlowNodeType(nodeType),
        position: {
          x: Math.random() * 400 + 100,
          y: Math.random() * 400 + 100
        },
        data: {
          label: getNodeLabel(nodeType),
          nodeType: nodeType,
          config: {}
        }
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes]
  );

  // Handle drag over (required for drop to work)
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop on canvas
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData('application/reactflow');
      
      // Check if it's a valid node drop
      if (!nodeType) {
        return;
      }

      // Get the drop position relative to the React Flow canvas
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: `node_${Date.now()}`,
        type: getReactFlowNodeType(nodeType),
        position,
        data: {
          label: getNodeLabel(nodeType),
          nodeType: nodeType,
          config: {}
        }
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, setNodes]
  );

  // Get React Flow node type from customAgent node type
  const getReactFlowNodeType = (nodeType) => {
    if (nodeType.startsWith('trigger.')) return 'trigger';
    if (nodeType.startsWith('cognition.')) return 'cognition';
    if (nodeType.startsWith('action.')) return 'action';
    return 'action';
  };

  // Get readable node label
  const getNodeLabel = (nodeType) => {
    const labels = {
      // Signals
      'trigger.buyer_intent': 'Buyer Intent',
      'trigger.deal_health': 'Deal Health',
      
      // Cognition
      'cognition.conditions': 'Conditions',
      'cognition.generation': 'Generation',
      
      // Actions
      'action.email': 'Email',
      'action.messaging': 'Messaging',
      'action.documents': 'Documents',
      'action.velox': 'Velox'
    };
    return labels[nodeType] || nodeType;
  };

  const getNodeTypeKey = (node) => node?.data?.nodeType || node?.type;

  const applyNodeConfigDefaults = (node, config = {}) => {
    const nodeTypeKey = getNodeTypeKey(node);

    if (nodeTypeKey === 'cognition.generation') {
      const normalized = { ...config };
      if (!normalized.generationType) {
        normalized.generationType = 'text_generation';
      }
      if (!normalized.model) {
        normalized.model = 'gpt-4';
      }
      if (
        normalized.temperature === undefined ||
        normalized.temperature === null ||
        normalized.temperature === ''
      ) {
        normalized.temperature = '0.7';
      }
      return normalized;
    }

    return config;
  };

  const prepareNodesForSave = (nodesList = []) =>
    nodesList.map((node) => ({
      ...node,
      data: {
        ...node.data,
        config: applyNodeConfigDefaults(node, node.data?.config || {})
      }
    }));

  const buildTriggerPayload = (nodesList = []) => {
    if (!Array.isArray(nodesList)) return null;

    const triggerNode =
      nodesList.find((node) => {
        const nodeType = getNodeTypeKey(node);
        return nodeType && nodeType.startsWith('trigger.');
      }) ||
      nodesList.find((node) => node.type === 'trigger');

    if (!triggerNode || !triggerNode.data) {
      return null;
    }

    const triggerType =
      getNodeTypeKey(triggerNode) ||
      triggerNode.data?.type ||
      triggerNode.type;

    if (!triggerType) {
      return triggerNode.data;
    }

    // Ensure config is included with triggerType for backend to detect one-time triggers
    const config = triggerNode.data?.config || {};
    
    return {
      ...triggerNode.data,
      type: triggerType,
      config: {
        ...config,
        // Ensure triggerType is in config for backend detection
        triggerType: config.triggerType || 'always_on'
      }
    };
  };

  // Save customAgent
  const handleSave = async (name, description) => {
    try {
      console.log('handleSave called with:', { name, description, currentCustomAgent: currentCustomAgent?.id, nodesCount: nodes.length });
      setSaving(true);

      // Use provided name or current customAgent name, but don't use empty string
      const customAgentName = name?.trim() || currentCustomAgent?.name || 'Untitled Custom Agent';
      const customAgentDescription = description !== undefined ? description : (currentCustomAgent?.description || '');

      const preparedNodes = prepareNodesForSave(nodes);

      const customAgentData = {
        name: customAgentName,
        description: customAgentDescription,
        nodes: preparedNodes, // Always include current nodes with all configs
        edges: edges,
        trigger: buildTriggerPayload(preparedNodes),
        executionMode: currentCustomAgent?.executionMode || 'manual'
      };

      // Determine if this is an existing customAgent or new one
      // Priority: 1) customAgentId from URL (if not "new"), 2) currentCustomAgent.id
      // If customAgentId is "new" OR both are missing, create new customAgent
      let existingCustomAgentId = null;
      let isNewCustomAgent = false;
      
      if (customAgentId && customAgentId !== 'new') {
        // URL has a valid customAgent ID - this is an existing customAgent
        existingCustomAgentId = customAgentId;
        isNewCustomAgent = false;
      } else if (currentCustomAgent?.id && customAgentId !== 'new') {
        // No customAgentId in URL but currentCustomAgent has an ID - use it
        existingCustomAgentId = currentCustomAgent.id;
        isNewCustomAgent = false;
      } else {
        // customAgentId is "new" or both are missing - create new customAgent
        existingCustomAgentId = null;
        isNewCustomAgent = true;
      }
      
      // Log for debugging
      console.log('Save decision:', {
        customAgentIdFromUrl: customAgentId,
        currentCustomAgentId: currentCustomAgent?.id,
        isNewCustomAgent: isNewCustomAgent,
        existingCustomAgentId: existingCustomAgentId,
        willUpdate: !!existingCustomAgentId,
        willCreate: isNewCustomAgent
      });
      
      console.log('Saving customAgent data:', { 
        customAgentIdFromUrl: customAgentId,
        currentCustomAgentId: currentCustomAgent?.id,
        existingCustomAgentId: existingCustomAgentId,
        name: customAgentName,
        nodesCount: customAgentData.nodes.length,
        hasConfigs: customAgentData.nodes.some(n => n.data?.config && Object.keys(n.data.config).length > 0)
      });

      let customAgent;
      if (existingCustomAgentId) {
        // Update existing customAgent - NO NAVIGATION
        console.log('Updating existing customAgent:', existingCustomAgentId);
        customAgent = await customAgentService.updateCustomAgent(existingCustomAgentId, customAgentData);
        console.log('CustomAgent updated successfully:', { id: customAgent?.id, name: customAgent?.name });
      } else {
        // Create new customAgent
        console.log('Creating new customAgent');
        customAgent = await customAgentService.createCustomAgent(customAgentData);
        console.log('CustomAgent created:', { id: customAgent?.id, name: customAgent?.name });
        // Navigate to the new customAgent's URL
        if (customAgent?.id) {
          console.log('Navigating to new customAgent:', customAgent.id);
          navigate(`/app/wizard/customAgents/${customAgent.id}`, { replace: true });
        }
      }

      // Update current customAgent with response, but preserve our nodes to keep configs
      if (customAgent) {
        console.log('Updating currentCustomAgent state with saved data');
        setCurrentCustomAgent({
          ...customAgent,
          name: customAgentName, // Ensure name is preserved
          nodes: nodes // Use current nodes state to preserve all configs
        });
        
        // Reload customAgent from backend to ensure we have the latest saved data
        // But only if we updated an existing customAgent (not a new one)
        if (existingCustomAgentId && customAgent.id) {
          console.log('Reloading customAgent to verify save');
          try {
            const reloaded = await customAgentService.getCustomAgent(customAgent.id);
            if (reloaded) {
              // Normalize nodes on reload
              const normalizedNodes = (reloaded.nodes || []).map(node => ({
                ...node,
                data: {
                  ...node.data,
                  config: node.data?.config || node.config || {}
                }
              }));
              setCurrentCustomAgent({
                ...reloaded,
                nodes: normalizedNodes
              });
              setNodes(normalizedNodes);
              console.log('CustomAgent reloaded successfully');
            }
          } catch (reloadErr) {
            console.error('Error reloading customAgent (non-critical):', reloadErr);
            // Non-critical error, continue
          }
        }
      }

      console.log('Save completed successfully');
      return true;
    } catch (err) {
      console.error('Error saving customAgent:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText
      });
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save customAgent';
      showError(errorMessage);
      // Don't show alert that might cause issues, just log
      console.error('Save failed:', errorMessage);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Handle inline name change
  const handleNameChange = async (newName) => {
    if (!currentCustomAgent || !newName.trim()) return;

    try {
      // Update name along with current nodes and edges to preserve everything
      const preparedNodes = prepareNodesForSave(nodes);
      const customAgentData = {
        name: newName.trim(),
        description: currentCustomAgent.description || '',
        nodes: preparedNodes, // Preserve all nodes with configs
        edges: edges,
        trigger: buildTriggerPayload(preparedNodes),
        executionMode: currentCustomAgent.executionMode || 'manual'
      };
      
      const response = await customAgentService.updateCustomAgent(currentCustomAgent.id, customAgentData);
      // Preserve nodes when updating
      setCurrentCustomAgent({
        ...response.data,
        name: newName.trim(),
        nodes: nodes // Use current nodes to preserve configs
      });
    } catch (err) {
      console.error('Error updating customAgent name:', err);
      showError('Failed to update customAgent name');
    }
  };

  // Open deploy modal
  const handleDeploy = () => {
    if (!currentCustomAgent) {
      showError('Please save the customAgent before deploying');
      return;
    }
    
    // Check if customAgent is already active
    // For one-time triggers, allow deployment even when active (they should be paused after execution)
    const triggerNode = nodes.find((node) => {
      const nodeType = node.data?.nodeType;
      return nodeType && nodeType.startsWith('trigger.');
    });
    const triggerType = triggerNode?.data?.config?.triggerType || 'always_on';
    const isOneTimeTrigger = triggerType === 'one_time';
    
    if (currentCustomAgent.status === 'active' && !isOneTimeTrigger) {
      showError('CustomAgent is already active. Please pause it first if you want to redeploy.');
      return;
    }
    
    setShowDeployModal(true);
  };

  // Deploy customAgent with configuration - handles both immediate and scheduled
  const handleDeployWithConfig = async (deploymentConfig) => {
    try {
      setLoading(true);
      setDeployStatus('deploying'); // Add deployment status
      setShowDeployModal(false);

      // Find the trigger node to get its configuration
      const triggerNode = nodes.find((node) => {
        const nodeType = node.data?.nodeType;
        return nodeType && nodeType.startsWith('trigger.');
      });

      if (!triggerNode) {
        showError('CustomAgent must have a trigger node configured');
        setLoading(false);
        return;
      }

      const triggerType = triggerNode.data?.config?.triggerType || 'always_on';
      const triggerNodeType = triggerNode.data?.nodeType;

      // Update customAgent with execution mode and schedule if scheduled
      if (deploymentConfig.executionMode === 'scheduled') {
        await customAgentService.updateCustomAgent(currentCustomAgent.id, {
          executionMode: deploymentConfig.executionMode,
          schedule: deploymentConfig.schedule
        });
      } else {
        // For immediate deployment, set execution mode to 'immediate'
        await customAgentService.updateCustomAgent(currentCustomAgent.id, {
          executionMode: 'immediate'
        });
      }

      // Deploy the customAgent
      setDeployStatus('deploying');
      await customAgentService.deployCustomAgent(currentCustomAgent.id);
      setDeployStatus('triggering');

      // If it's immediate deployment and a one-time trigger, execute it immediately
      if (deploymentConfig.executionMode === 'immediate' && triggerType === 'one_time') {
        const triggerConfig = triggerNode.data?.config || {};
        
        // Get accountIds - can include 'all' or specific account IDs
        const accountIds = Array.isArray(triggerConfig.accountId) 
          ? triggerConfig.accountId 
          : (triggerConfig.accountId ? [triggerConfig.accountId] : []);
        
        if (accountIds.length === 0) {
          showError('Please select at least one account or "All Companies"');
          setLoading(false);
          await loadCustomAgent();
          return;
        }
        
        // Prepare trigger data based on trigger node type
        let triggerData = {};
        
        try {
          if (triggerNodeType === 'trigger.buyer_intent') {
            triggerData = {
              accountId: accountIds,
              intentType: triggerConfig.intentType || 'engagement',
              conditions: triggerConfig.conditions || '',
              dealStageFilter: triggerConfig.dealStageFilter || ['all']
            };
            
            const result = await customAgentService.triggerBuyerIntent(currentCustomAgent.id, triggerData);
            
            // Show success message with execution count if multiple accounts
            const executionCount = result?.data?.executionCount || 1;
            setDeployStatus('success');
            const message = executionCount > 1 
              ? `CustomAgent deployed and triggered successfully for ${executionCount} accounts!`
              : 'CustomAgent deployed and triggered successfully!';
            alert(message);
            setTimeout(() => setDeployStatus(null), 3000);
          } else if (triggerNodeType === 'trigger.deal_health') {
            triggerData = {
              accountId: accountIds,
              healthMetric: triggerConfig.healthMetric || 'overall',
              conditions: triggerConfig.conditions || triggerConfig.triggerCondition || '',
              dealStageFilter: triggerConfig.stageFilter || triggerConfig.dealStageFilter || ['all']
            };
            
            const result = await customAgentService.triggerDealHealth(currentCustomAgent.id, triggerData);
            
            // Show success message with execution count if multiple accounts
            const executionCount = result?.data?.executionCount || 1;
            setDeployStatus('success');
            const message = executionCount > 1 
              ? `CustomAgent deployed and triggered successfully for ${executionCount} accounts!`
              : 'CustomAgent deployed and triggered successfully!';
            alert(message);
            setTimeout(() => setDeployStatus(null), 3000);
          }

          // For one-time triggers, wait a moment for backend to update status, then reload
          // Backend should set status to 'paused' after execution
          await new Promise(resolve => setTimeout(resolve, 500)); // Small delay to ensure backend has updated
          await loadCustomAgent();
        } catch (triggerErr) {
          // If trigger fails, keep customAgent deployed but show error
          console.error('Error triggering customAgent:', triggerErr);
          setDeployStatus('error');
          await loadCustomAgent();
          const errorMessage = triggerErr.response?.data?.message || triggerErr.message || 'CustomAgent deployed but trigger failed';
          showError(errorMessage);
          alert(`CustomAgent deployed but trigger failed: ${errorMessage}`);
          setTimeout(() => setDeployStatus(null), 5000);
        }
      } else {
        // For scheduled or always-on immediate triggers, reload to update status
        setDeployStatus('success');
        await loadCustomAgent();
        const message = deploymentConfig.executionMode === 'scheduled' 
          ? 'CustomAgent scheduled successfully!'
          : 'CustomAgent deployed successfully!';
        alert(message);
        setTimeout(() => setDeployStatus(null), 3000);
      }
    } catch (err) {
      console.error('Error deploying customAgent:', err);
      setDeployStatus('error');
      showError(err.response?.data?.message || 'Failed to deploy customAgent');
      setTimeout(() => setDeployStatus(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Pause customAgent
  const handlePause = async () => {
    if (!currentCustomAgent) return;

    try {
      setLoading(true);
      await customAgentService.pauseCustomAgent(currentCustomAgent.id);
      await loadCustomAgent();
      alert('CustomAgent paused successfully!');
    } catch (err) {
      console.error('Error pausing customAgent:', err);
      showError(err.response?.data?.message || 'Failed to pause customAgent');
    } finally {
      setLoading(false);
    }
  };

  // Update node configuration
  const handleNodeConfigSave = async (nodeId, config) => {
    console.log('handleNodeConfigSave called:', { nodeId, config, currentCustomAgent: currentCustomAgent?.id });
    
    const targetNode = nodes.find((node) => node.id === nodeId);
    const normalizedConfig = applyNodeConfigDefaults(targetNode, config);

    // Update local state first
    const updatedNodes = nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              config: normalizedConfig
            }
          };
        }
        return node;
    });
    
    setNodes(updatedNodes);
    
    // Save to backend if customAgent exists, otherwise create it
    try {
      setSaving(true);
      
      const preparedNodes = prepareNodesForSave(updatedNodes);

      const customAgentData = {
        name: currentCustomAgent?.name || 'Untitled Custom Agent',
        description: currentCustomAgent?.description || '',
        nodes: preparedNodes,
        edges,
        trigger: buildTriggerPayload(preparedNodes),
        executionMode: currentCustomAgent?.executionMode || 'manual'
        // Note: We don't send 'status' here - backend preserves existing status
        // Status should only change when explicitly deploying/pausing via deploy/pause endpoints
      };
      
      // Determine if this is an existing customAgent or new one
      // Priority: 1) customAgentId from URL (if not "new"), 2) currentCustomAgent.id
      // If customAgentId is "new" OR both are missing, create new customAgent
      let existingCustomAgentId = null;
      let isNewCustomAgent = false;
      
      if (customAgentId && customAgentId !== 'new') {
        // URL has a valid customAgent ID - this is an existing customAgent
        existingCustomAgentId = customAgentId;
        isNewCustomAgent = false;
      } else if (currentCustomAgent?.id && customAgentId !== 'new') {
        // No customAgentId in URL but currentCustomAgent has an ID - use it
        existingCustomAgentId = currentCustomAgent.id;
        isNewCustomAgent = false;
      } else {
        // customAgentId is "new" or both are missing - create new customAgent
        existingCustomAgentId = null;
        isNewCustomAgent = true;
      }
      
      console.log('Node config save decision:', {
        customAgentIdFromUrl: customAgentId,
        currentCustomAgentId: currentCustomAgent?.id,
        isNewCustomAgent: isNewCustomAgent,
        existingCustomAgentId: existingCustomAgentId,
        willUpdate: !!existingCustomAgentId,
        willCreate: isNewCustomAgent
      });
      
      console.log('Saving node config, customAgentData:', {
        customAgentIdFromUrl: customAgentId,
        currentCustomAgentId: currentCustomAgent?.id,
        existingCustomAgentId: existingCustomAgentId,
        nodesCount: customAgentData.nodes.length
      });
      
      let customAgent;
      if (existingCustomAgentId) {
        // Update existing customAgent - NO NAVIGATION
        console.log('Updating customAgent for node config save:', existingCustomAgentId);
        customAgent = await customAgentService.updateCustomAgent(existingCustomAgentId, customAgentData);
        console.log('Node config saved successfully');
      } else {
        // Create new customAgent if it doesn't exist
        console.log('Creating customAgent for node config save');
        customAgent = await customAgentService.createCustomAgent(customAgentData);
        console.log('CustomAgent created for node config:', customAgent?.id);
        // Only navigate for new customAgents, and only if we're not already on a customAgent route
        // Use absolute path to ensure correct routing
        if (customAgent?.id) {
          console.log('Navigating to new customAgent route');
          navigate(`/app/wizard/customAgents/${customAgent.id}`, { replace: true });
        }
      }

      // Update current customAgent with response, but preserve our updated nodes to keep configs
      if (customAgent) {
        setCurrentCustomAgent({
          ...customAgent,
          nodes: updatedNodes // Use our updated nodes to preserve configs
        });
      }
    } catch (err) {
      console.error('Error saving node configuration:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save node configuration';
      showError(errorMessage);
      // Don't close the panel if there's an error so user can retry
      alert(`Error saving configuration: ${errorMessage}`);
      return;
    } finally {
      setSaving(false);
    }
    
    setIsConfigPanelOpen(false);
    setSelectedNode(null);
  };

  if (loading && !currentCustomAgent) {
    return (
      <div className="custom-agent-builder-loading">
        <div className="spinner"></div>
        <p>Loading customAgent...</p>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/app/wizard');
  };

  return (
    <div className="custom-agent-builder">
      <CustomAgentToolbar
        customAgent={currentCustomAgent}
        onSave={handleSave}
        onDeploy={handleDeploy}
        onPause={handlePause}
        onBack={handleBack}
        onNameChange={handleNameChange}
        saving={saving}
        isActive={currentCustomAgent?.status === 'active'}
        nodes={nodes}
        edges={edges}
        loading={loading}
        deployStatus={deployStatus}
        onTest={() => setShowTestModal(true)}
      />

      <div className="custom-agent-main">
        <NodePalette
          isOpen={isPaletteOpen}
          onToggle={() => setIsPaletteOpen(!isPaletteOpen)}
          onNodeAdd={onNodeAdd}
        />

        <div 
          className="custom-agent-canvas-wrapper" 
          ref={reactFlowWrapper}
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodeDragStop={onNodeDragStop}
            onInit={setReactFlowInstance}
            fitView
            fitViewOptions={FIT_VIEW_OPTIONS}
          >
            <Background variant="dots" gap={12} size={1} />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        <NodeConfigPanel
          node={selectedNode}
          nodes={nodes}
          edges={edges}
          isOpen={isConfigPanelOpen}
          onClose={() => {
            setIsConfigPanelOpen(false);
            setSelectedNode(null);
            // Clear selection
            setNodes((nds) =>
              nds.map((n) => ({
                ...n,
                selected: false
              }))
            );
          }}
          onSave={handleNodeConfigSave}
          onDelete={handleDeleteNode}
          saving={saving}
        />
      </div>

      <DeployModal
        isOpen={showDeployModal}
        onClose={() => setShowDeployModal(false)}
        onDeploy={handleDeployWithConfig}
        customAgentName={currentCustomAgent?.name || 'Untitled Custom Agent'}
      />

      <TestCustomAgentModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        customAgent={currentCustomAgent}
        nodes={nodes}
        edges={edges}
      />
    </div>
  );
};

export default CustomAgentBuilder;

